import { useMemo, useState, ReactNode } from 'react';
import { css } from '@emotion/react';
import {
  AppointmentLeadTime,
  AppointmentMetricType,
  TimeFilterType,
  AppointmentStatus,
  AppointmentConfirmationStatus,
  AppointmentAggregatesResponse,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import dayjs from 'dayjs';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import {
  DataCard,
  DataCardProps,
  TooltipHeader,
  X_AXIS_TIME_FILTER_OPTIONS,
  formatDailyXAxisTick,
  formatTooltipTitle,
} from '@frontend/analytics-shared';
import { BarClick, Chart, ChartEditPanel } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { formatters } from '@frontend/shared';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Button, Heading, Text, Truncated } from '@frontend/design-system';
import { APPOINTMENT_TABS } from '../../constants';
import { useAppointmentAnalyticsV2ShallowStore } from '../../hooks/use-appointment-analytics-v2-store';
import { useBarClickRedirection } from '../../hooks/use-bar-click-redirection';
import { APPOINTMENT_CONFIRMATION_STATUS_Y_AXIS_GROUPS } from '../../utils/appointment-confirmation-status-options';
import { APPOINTMENT_LEAD_TIME_Y_AXIS_GROUPS } from '../../utils/appointment-lead-time-options';
import { createBarCountTooltipData, getLegendCountsFromSummary } from '../../utils/appointment-tooltip-utils';
import { buildChartSubtitle } from '../../utils/build-chart-subtitle';

const colors = {
  completed: theme.colors.success30,
  noShow: theme.colors.tangerine30,
  cancelled: theme.colors.critical40,
  confirmed: theme.colors.primary30,
  unconfirmed: theme.colorsV2.yellow30(),
  unknown: theme.colors.neutral30,
};

// Darker shades used for the count text in the tooltip so it stays legible
// against the lighter bar fill colors above.
const labelColors: Record<string, string> = {
  completed: theme.colors.success50,
  noShow: theme.colors.tangerine50,
  cancelled: theme.colors.critical50,
};

const labels = {
  completed: 'Completed',
  noShow: 'No-Show',
  cancelled: 'Cancelled',
};

type BreakdownChartYAxes = {
  leadTime: AppointmentLeadTime[];
  confirmationStatus: AppointmentConfirmationStatus[];
};

const defaultYAxisSelected: BreakdownChartYAxes = {
  leadTime: [],
  confirmationStatus: [],
};

interface Props {
  aggregatesData: AppointmentAggregatesResponse | undefined;
  aggregatesDataLoading: boolean;
  renderOnlyChart?: boolean;
  filters?: AnalyticsGatewayV1.Types.GetAppointmentsDataIO['input'];
}
export const AppointmentBreakdownChart = (props: Props) => {
  const { aggregatesData, aggregatesDataLoading, filters: propsFilters, renderOnlyChart } = props;
  const { t } = useTranslation('analytics');
  const { filtersInitialized, filters, filterHintText, applyFiltersAndSwitchToTableView } =
    useAppointmentAnalyticsV2ShallowStore(
      'filtersInitialized',
      'filters',
      'filterHintText',
      'applyFiltersAndSwitchToTableView'
    );
  const handleBarClickRedirect = useBarClickRedirection();
  const [selectedXAxis, setSelectedXAxis] = useState<TimeFilterType>(TimeFilterType.DAILY);
  const [selectedYAxes, setSelectedYAxes] = useState<Record<string, string[]>>(defaultYAxisSelected);
  const navigate = useNavigate();

  const currentFilters = filters[APPOINTMENT_TABS.PAST];

  const getConfirmationStatusParam = () => {
    const selected = selectedYAxes.confirmationStatus;
    if (selected?.length === 1) {
      return selected[0] as AppointmentConfirmationStatus;
    }
    if (selected?.length === 2) {
      return AppointmentConfirmationStatus.APPOINTMENT_CNF_STATUS_UNSPECIFIED;
    }
    return undefined;
  };

  const isQueryEnabled =
    propsFilters !== undefined
      ? (propsFilters.locationId?.length ?? 0) > 0
      : filtersInitialized && (currentFilters?.locationId?.length ?? 0) > 0;

  const { data: appointmentsData, isLoading } = AnalyticsGatewayV1.Queries.useGetAppointmentsDataQuery(
    propsFilters
      ? { ...propsFilters, metricType: AppointmentMetricType.APPOINTMENT_BREAKDOWN }
      : {
          ...currentFilters,
          metricType: AppointmentMetricType.APPOINTMENT_BREAKDOWN,
          filterType: selectedXAxis,
          leadTime: selectedYAxes.leadTime?.[0] as AppointmentLeadTime | undefined,
          confirmationStatus: getConfirmationStatusParam(),
        },
    {
      enabled: isQueryEnabled,
    }
  );

  // Chart Data mapped from API
  const chartData = useMemo(() => {
    if (!appointmentsData?.data) {
      return { groups: [], stackKeys: [] };
    }

    const groups = appointmentsData.data.map((item) => {
      // Keep the raw name alongside the display name so tooltipHeadersByName can
      // look up summaryByRawName without having to reverse the transformation.
      const rawName = item.name ?? '';
      let name = rawName;
      if (selectedXAxis === TimeFilterType.HOURLY) {
        const hour = Number(name);
        if (!isNaN(hour)) {
          const h = hour % 12 || 12;
          name = `${h} ${hour < 12 ? 'am' : 'pm'}`;
        }
      } else if (selectedXAxis === TimeFilterType.DAY_OF_WEEK) {
        name = t(`${name}s`, { ns: 'analytics' });
      } else if (selectedXAxis === TimeFilterType.DAILY) {
        name = formatDailyXAxisTick(name);
      }

      return {
        name,
        rawName,
        values: {
          cancelled: Number(item.cancelled || 0),
          noShow: Number(item.noShow || 0),
          completed: Number(item.completed || 0),
        },
      };
    });

    return {
      groups,
      stackKeys: ['cancelled', 'completed', 'noShow'],
    };
  }, [appointmentsData, selectedXAxis, t]);

  const tooltipTitlesByName = useMemo(() => {
    return (
      chartData?.groups?.reduce<Record<string, ReactNode>>((acc, group) => {
        const rawName = group.rawName;

        acc[group.name] = formatTooltipTitle(rawName, selectedXAxis);
        return acc;
      }, {}) ?? {}
    );
  }, [chartData.groups, selectedXAxis]);

  const tooltipHeadersByName = useMemo(() => {
    return chartData.groups.reduce<Record<string, JSX.Element>>((acc, group) => {
      const total = Object.values(group.values).reduce((sum, val) => sum + val, 0);
      const tooltipTitle = tooltipTitlesByName[group.name] ?? group.name;

      let finalFilterHintText;
      if (selectedXAxis === TimeFilterType.DAILY) {
        finalFilterHintText = dayjs(group.rawName).format('dddd');
      }

      acc[group.name] = (
        <TooltipHeader tooltipTitle={tooltipTitle} filterHintText={finalFilterHintText} total={total} />
      );

      return acc;
    }, {});
  }, [chartData.groups, selectedXAxis, tooltipTitlesByName]);

  // Renders the count value for each stack key in a darker label color instead
  // of the lighter bar fill color used by the default tooltip.
  const customTooltipData = useMemo(
    () =>
      createBarCountTooltipData({
        groups: chartData.groups,
        stackKeys: chartData.stackKeys,
        labelColors,
        labels,
      }),
    [chartData.groups, chartData.stackKeys]
  );

  // Count shown beside each legend label, sourced from the API `summary`.
  const legendValues = useMemo(() => {
    const values = getLegendCountsFromSummary(appointmentsData?.summary);
    return {
      ...values,
      noShow: values['no_show'] ?? 0, // Rename key to match stack key for legend
    };
  }, [appointmentsData?.summary]);

  const aggData = aggregatesData?.data ? aggregatesData.data[0] : undefined;
  const breakdownCardsData: DataCardProps[] = useMemo(
    () => [
      {
        label: t('Cancelled'),
        value: formatters.value(Number(aggData?.cancelledAppointments)) ?? 0,
        iconData: {
          name: 'block',
          color: theme.colors.critical60,
          bgColor: theme.colors.critical5,
        },
        ctaConfig: {
          icon: 'trend-up',
          text: t('View'),
          onClick: () => {
            applyFiltersAndSwitchToTableView({
              status: [AppointmentStatus.APPOINTMENT_STATUS_CANCELLED],
            });
          },
        },
        isLoading: aggregatesDataLoading,
        infoTipData: {
          description: t('Number of appointments mapped to canceled status in Weave'),
        },
      },
      {
        label: t('No-Show'),
        value: formatters.value(Number(aggData?.noShowCount)) ?? 0,
        iconData: {
          name: 'user-x',
          color: theme.colors.tangerine50,
          bgColor: theme.colors.tangerine10,
        },
        ctaConfig: {
          icon: 'trend-up',
          text: t('View'),
          onClick: () => {
            applyFiltersAndSwitchToTableView({
              status: [AppointmentStatus.APPOINTMENT_STATUS_NSR],
            });
          },
        },
        isLoading: aggregatesDataLoading,
        infoTipData: {
          description: t('Number of appointments marked as No-show or not marked as complete'),
        },
      },
      {
        label: t('Unconfirmed'),
        value: formatters.value(Number(aggData?.unconfirmedAppointments)) ?? 0,
        iconData: {
          name: 'pending',
          color: theme.colorsV2.yellow70(),
          bgColor: theme.colorsV2.yellow20(25),
        },
        ctaConfig: {
          icon: 'trend-up',
          text: t('View'),
          onClick: () => {
            applyFiltersAndSwitchToTableView({
              confirmationStatus: AppointmentConfirmationStatus.APPOINTMENT_CNF_STATUS_UNCONFIRMED,
            });
          },
        },
        isLoading: aggregatesDataLoading,
        infoTipData: {
          description: t('Number of appointments not marked as confirmed'),
        },
      },
      {
        label: t('Confirmed'),
        value: formatters.value(Number(aggData?.confirmedAppointments)) ?? 0,
        iconData: {
          name: 'calendar-confirmed-small',
          color: theme.colors.primary50,
          bgColor: theme.colors.primary5,
        },
        ctaConfig: {
          icon: 'trend-up',
          text: t('View'),
          onClick: () => {
            applyFiltersAndSwitchToTableView({
              confirmationStatus: AppointmentConfirmationStatus.APPOINTMENT_CNF_STATUS_CONFIRMED,
            });
          },
        },
        isLoading: aggregatesDataLoading,
        infoTipData: {
          description: t('Total number of appointments confirmed by patient or provider'),
        },
      },
    ],
    [applyFiltersAndSwitchToTableView, aggData, aggregatesDataLoading, t]
  );

  const yAxisOptions = useMemo(
    () =>
      [...APPOINTMENT_CONFIRMATION_STATUS_Y_AXIS_GROUPS, ...APPOINTMENT_LEAD_TIME_Y_AXIS_GROUPS].flatMap(
        (g) => g.options
      ),
    []
  );

  const subtitle = useMemo(
    () =>
      buildChartSubtitle({
        selectedYAxes,
        yAxisOptions,
        suffixMap: { leadTime: 'lead time' },
        filterHintText,
        t,
      }),
    [t, selectedYAxes, filterHintText, yAxisOptions]
  );

  const onXChange = (xAxis: string) => {
    setSelectedXAxis(xAxis as TimeFilterType);
  };

  const onYChange = (yAxes: Record<string, string[]>) => {
    setSelectedYAxes(yAxes);
  };

  const handleOnBarClick = (args: BarClick) => {
    const statusMapping: Record<string, AppointmentStatus> = {
      cancelled: AppointmentStatus.APPOINTMENT_STATUS_CANCELLED,
      completed: AppointmentStatus.APPOINTMENT_STATUS_COMPLETED,
      noShow: AppointmentStatus.APPOINTMENT_STATUS_NSR,
    };
    const status = statusMapping[args.barId];

    handleBarClickRedirect(
      args,
      chartData.groups,
      status
        ? {
            status: [status],
            leadTime: selectedYAxes.leadTime?.[0] as AppointmentLeadTime | undefined,
            confirmationStatus: getConfirmationStatusParam(),
          }
        : {}
    );
  };

  const chart = (
    <Chart
      colors={colors}
      labels={labels}
      style={!renderOnlyChart ? { marginBottom: theme.spacing(0) } : undefined}
      isLoading={isLoading}
      css={{
        '.chart-content': {
          padding: theme.spacing(renderOnlyChart ? 3 : 2),
        },
      }}
    >
      <Chart.HeaderV2
        title={
          <Text weight='bold' size='large' css={renderOnlyChart ? { fontSize: theme.spacing(2.5) } : undefined}>
            {t('Appointments by status')}
          </Text>
        }
        subTitle={
          !renderOnlyChart && subtitle ? (
            <Truncated color='subdued' as={Text} size='medium' css={{ maxWidth: '700px' }}>
              {subtitle}
            </Truncated>
          ) : undefined
        }
        action={
          renderOnlyChart ? (
            <Button
              size='small'
              variant='tertiary'
              onClick={() => {
                navigate({
                  to: '/analytics/appointment',
                  search: (prev: any) => ({ ...prev }),
                });
              }}
              trackingId='show-more-nsr-chart-appointment-analytics'
            >
              {t('Show More')}
            </Button>
          ) : (
            <ChartEditPanel
              xAxisOptions={X_AXIS_TIME_FILTER_OPTIONS}
              yAxisGroups={[...APPOINTMENT_LEAD_TIME_Y_AXIS_GROUPS, ...APPOINTMENT_CONFIRMATION_STATUS_Y_AXIS_GROUPS]}
              defaultYAxisSelected={defaultYAxisSelected}
              defaultXAxisSelected={TimeFilterType.DAILY}
              onYChange={onYChange}
              onXChange={onXChange}
            />
          )
        }
      />
      <Chart.BarChart
        data={chartData}
        appearance={{
          mode: 'stacked',
          barSize: 24,
          showXAxis: true,
          showYAxis: true,
          height: renderOnlyChart ? 280 : 240,
          barRadius: 4,
          hideMarkers: true,
          customTooltipTitle: ({ groupName }) =>
            tooltipHeadersByName[groupName] ?? tooltipTitlesByName[groupName] ?? groupName,
          customTooltipData,
        }}
        onClick={!renderOnlyChart && selectedXAxis === TimeFilterType.DAILY ? handleOnBarClick : undefined}
      />
      <Chart.Legends doNotCollapseLegends values={legendValues} />
    </Chart>
  );

  if (renderOnlyChart) {
    return chart;
  }

  return (
    <div css={styles.outerContainer}>
      <Heading
        level={3}
        css={{
          lineHeight: theme.spacing(3),
        }}
      >
        {t('Number of Appointments by Status')}
      </Heading>

      <div css={styles.cardsGrid}>
        {breakdownCardsData.map((card) => (
          <DataCard key={card.label} {...card} />
        ))}
      </div>

      {chart}
    </div>
  );
};

const styles = {
  outerContainer: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(2)};
    border: 1px solid ${theme.colors.neutral20};
    border-radius: ${theme.spacing(1)};
  `,
  cardsGrid: css`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: ${theme.spacing(2)};

    @media (max-width: ${breakpoints.large.max}px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: ${breakpoints.medium.max}px) {
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    }
  `,
};
